'use client'

import React from 'react'

// ============================================================================
// Types
// ============================================================================

interface TableColumn {
  key: string
  label: string
  sortable?: boolean
}

interface TableData {
  columns: TableColumn[]
  rows: Array<Record<string, unknown>>
  total_rows?: number
}

interface DataTableWidgetProps {
  data: TableData
  title?: string
}

// ============================================================================
// Data Table Widget
// ============================================================================

export function DataTableWidget({ data, title }: DataTableWidgetProps) {
  const { columns, rows, total_rows } = data

  if (!columns || columns.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        {title && <h3 className="font-semibold text-brand-navy mb-2">{title}</h3>}
        <p className="text-sm text-gray-500">No columns defined</p>
      </div>
    )
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        {title && <h3 className="font-semibold text-brand-navy mb-2">{title}</h3>}
        <p className="text-sm text-gray-500">No data available</p>
      </div>
    )
  }

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
      {title && (
        <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h3 className="font-semibold text-brand-navy">{title}</h3>
          {total_rows && total_rows > rows.length && (
            <p className="text-xs text-gray-500 mt-0.5">
              Showing {rows.length} of {total_rows} rows
            </p>
          )}
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-2 text-left font-medium text-gray-700"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
              >
                {columns.map((col) => (
                  <td key={col.key} className="px-4 py-2 text-gray-900">
                    {formatCellValue(row[col.key])}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ============================================================================
// Helper Functions
// ============================================================================

function formatCellValue(value: unknown): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-gray-400">—</span>
  }

  if (typeof value === 'boolean') {
    return value ? (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
        Yes
      </span>
    ) : (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
        No
      </span>
    )
  }

  if (typeof value === 'object') {
    return <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{JSON.stringify(value)}</code>
  }

  return String(value)
}
